import { differenceInSeconds, endOfDay, startOfDay } from 'date-fns';
import format from 'date-fns/format';
import { flatMap, groupBy, values } from 'lodash-es';

import { COUPON_TYPE_OPTIONS, CouponMold, ProductCouponMold } from '../type';

export interface InvalidCouponMold {
  /** 쿠폰 id */
  id: string | number;
  /** 등록 실패 원인 */
  errorType?: ErrorType;
  /** 유효성 검증 문구 */
  message?: string;
  /** true시 등록 불가  */
  isError?: boolean;
}

interface ExhibitionInfo {
  startAt?: string;
  endAt?: string;
}

export const INVALID_MESSAGE = {
  NOT_FOUND: '등록 불가한 쿠폰이 있습니다',
  NOT_DOWNLOAD_TYPE: '적용할 수 없는 쿠폰입니다 - 다운로드/첫구매/비공개만 가능',
  DURATION_NOT_START: '쿠폰의 발행시작일이 도래하지 않은 쿠폰이 있습니다.',
  DURATION_END: '기간이 만료된 쿠폰이 있습니다.',
  INVALID_START_AT: '쿠폰의 발행 시작일이 기획전 시작일보다 느린 쿠폰이 있습니다.',
  INVALID_END_AT: '기획전 종료일 보다 발행/발급 종료일이 빠르게 설정된 쿠폰이 있습니다.',
  UNABLE_PUBLISH: '발행이 중단/중지된 쿠폰입니다.',
} as const;

export type ErrorType = keyof typeof INVALID_MESSAGE;

interface InvalidMessage {
  publishType: string;
  publishState?: string;
  autoPublish?: boolean;
  startAt?: Date;
  endAt?: Date;
  exhibitionInfo?: ExhibitionInfo;
}

function dateFormat(date: string | Date, type: string): string {
  if (typeof date === 'string') {
    return format(new Date(date), type);
  }

  return format(date, type);
}

function getInvalidMessage({
  publishType,
  publishState,
  autoPublish,
  startAt,
  endAt,
  exhibitionInfo,
}: InvalidMessage): Pick<InvalidCouponMold, 'errorType' | 'isError'> {
  const durationStartDiff = startAt ? differenceInSeconds(new Date(), startAt) : undefined;
  const durationEndDiff = endAt ? differenceInSeconds(endAt, new Date()) : undefined;

  const publishStartDiff = exhibitionInfo?.startAt && startAt
    ? differenceInSeconds(new Date(exhibitionInfo.startAt), new Date(startAt))
    : undefined;

  const publishEndDiff = exhibitionInfo?.endAt && endAt
    ? differenceInSeconds(new Date(endAt), new Date(exhibitionInfo?.endAt))
    : undefined;


  let isError: boolean | undefined;
  let errorType: ErrorType | undefined;

  switch (true) {
    // 다운로드/첫구매/비공개만 가능
    case !(publishType === 'DOWNLOAD' || publishType === 'FIRST_ORDER' || (publishType === 'HIDDEN' && !autoPublish)):
      errorType = 'NOT_DOWNLOAD_TYPE';
      isError = true;
      break;
    // now() < 발행/발급 시작일인 경우
    case durationStartDiff && durationStartDiff < 0:
      errorType = 'DURATION_NOT_START';
      isError = false;
      break;
    // now() > 발행/발급 종료일인 경우
    case durationEndDiff && durationEndDiff < 0:
      errorType = 'DURATION_END';
      isError = true;
      break;
    // 기획전 시작일 < 상품/장바구니 쿠폰 발행시작일인 경우
    case publishStartDiff && publishStartDiff < 0:
      errorType = 'INVALID_START_AT';
      isError = false;
      break;
    // 기획전 종료일 > 쿠폰 발행/발급 종료일인 경우
    case publishEndDiff && publishEndDiff < 0:
      errorType = 'INVALID_END_AT';
      isError = false;
      break;
    case publishState && publishState !== 'PUBLISHABLE':
      errorType = 'UNABLE_PUBLISH';
      isError = true;
      break;
    default:
  }
  return {
    isError,
    errorType,
  };
}

function getGroupedErrorType(couponMolds: InvalidCouponMold[]): InvalidCouponMold[] {
  const groupedInvalidCouponMolds: Record<string, InvalidCouponMold[]> =
  couponMolds
    .filter(({ errorType }) => errorType)
    .reduce((acc, obj) => {
      const { errorType = 'DURATION_END' } = obj;
      if (acc[errorType]) {
        acc[errorType].push(obj);
      } else {
        acc[errorType] = [obj];
      }
      return acc;
    }, {} as Record<string, InvalidCouponMold[]>);

  return flatMap(Object.values(groupedInvalidCouponMolds));
}

export function getInvalidCouponMolds(
  couponMolds: CouponMold[],
  exhibitionInfo?: ExhibitionInfo,
): InvalidCouponMold[] {
  const invalidCoupons = couponMolds.map((couponMold) => {
    const { id, title, type: publishType, publishStartAt, publishEndAt, autoPublish } = couponMold;

    const startAtWithTime = publishStartAt ? startOfDay(new Date(publishStartAt)) : undefined;
    const endAtWithTime = publishEndAt ? endOfDay(new Date(publishEndAt)) : undefined;

    let message: string | undefined;

    const { errorType, isError } = getInvalidMessage({
      publishType,
      autoPublish,
      startAt: startAtWithTime,
      endAt: endAtWithTime,
      exhibitionInfo,
    });

    switch (errorType) {
      case 'NOT_DOWNLOAD_TYPE':
        message = `${id} - ${COUPON_TYPE_OPTIONS.find(({ value }) => value === publishType)?.label}`;
        break;
      case 'INVALID_START_AT':
        message = `${id} ${title} - ${publishStartAt ? dateFormat(publishStartAt, 'MM/dd') : ''} 발행 시작`;
        break;
      case 'DURATION_NOT_START':
        message = `${id} ${title} - ${publishStartAt ? dateFormat(publishStartAt, 'MM/dd') : ''} 발행 시작`;
        break;
      case 'DURATION_END':
        message = `${id} ${title} - 기간 종료`;
        break;
      case 'INVALID_END_AT':
        message = `${id} ${title} - ${publishEndAt ? dateFormat(publishEndAt, 'MM/dd') : ''} 발행 종료`;
        break;
      default:
    }

    return {
      id,
      errorType,
      message: message && `장바구니 쿠폰 : ${message}`,
      isError,
    };
  });

  return getGroupedErrorType(invalidCoupons);
}

export function getInvalidProductCouponMolds(
  couponMolds: ProductCouponMold[],
  exhibitionInfo?: ExhibitionInfo,
): InvalidCouponMold[] {
  const invalidCoupons = couponMolds.map((couponMold) => {
    const { couponMoldId: id, couponName, publishType, publishState, publishStartDate, publishEndDate } = couponMold;

    let message: string | undefined;

    const startAt = publishStartDate ? new Date(publishStartDate) : undefined;
    const endAt = publishEndDate ? new Date(publishEndDate) : undefined;

    const { errorType, isError } = getInvalidMessage({
      publishType,
      publishState,
      startAt,
      endAt,
      exhibitionInfo,
    });

    switch (errorType) {
      case 'NOT_DOWNLOAD_TYPE':
        // 현재는 publishType에 DOWNLOAD만 존재
        message = `${id} - 다운로드형이 아닙니다.`;
        break;
      case 'INVALID_START_AT':
        message = `${id} ${couponName} - ${startAt ? dateFormat(startAt, 'MM/dd HH:mm') : ''} 발행 시작`;
        break;
      case 'DURATION_NOT_START':
        message = `${id} ${couponName} - ${startAt ? dateFormat(startAt, 'MM/dd HH:mm') : ''} 발행 시작`;
        break;
      case 'DURATION_END':
        message = `${id} ${couponName} - 기간 종료`;
        break;
      case 'INVALID_END_AT':
        message = `${id} ${couponName} - ${endAt ? dateFormat(endAt, 'MM/dd HH:mm') : ''} 발행 종료`;
        break;
      case 'UNABLE_PUBLISH':
        message = `${id} ${couponName} - 발행중지된 쿠폰입니다.`;
        break;
      default:
    }

    return {
      id,
      errorType,
      message: message && `상품 쿠폰 : ${message}`,
      isError,
    };
  });

  return getGroupedErrorType(invalidCoupons);
}

export function getGroupedCouponMolds(
  groupedInvalidCartMolds: InvalidCouponMold[],
  groupedInvalidProductMolds: InvalidCouponMold[],
): InvalidCouponMold[][] {
  const mergedArray = [
    ...groupedInvalidCartMolds,
    ...groupedInvalidProductMolds,
  ];

  const groupedArray = values(groupBy(mergedArray, 'errorType'));

  return Object.values(groupedArray);
}
